/**
 * Utility to export an array of student records to a comprehensive CSV file.
 * Includes student demographics, academics, convocation registration, dues status,
 * complete address, bank refund details, document photo links, and feedback status.
 * @param {Array} students List of student objects
 */
export const exportStudentsToCSV = (students = []) => {
  const headers = [
    "Registration Number",
    "Student Name",
    "Gender",
    "Email Address",
    "Phone Number",
    "Faculty",
    "School",
    "Programme",
    "Specialization",
    "Batch",
    "CGPA",
    "Credits",
    "Registration Status",
    "Attendance Mode",
    "Convocation Slot / Date",
    "Companions",
    "Dues Status",
    "Pending Due Amount (INR)",
    "Feedback Status",
    "Address",
    "City",
    "District",
    "State",
    "Country",
    "Pincode",
    "Account Holder Name",
    "Bank Name",
    "Account Number",
    "IFSC Code",
    "Branch Name",
    "Bank Address",
    "Aadhaar Front Photo URL",
    "Aadhaar Back Photo URL",
    "Cancelled Cheque Photo URL",
    "Registered Date",
  ];

  const escapeCsv = (str) => {
    if (str === null || str === undefined) return '""';
    const clean = String(str).replace(/"/g, '""');
    return `"${clean}"`;
  };

  const rows = students.map((s) => {
    const isCourier =
      s.day?.toLowerCase() === "courier" || s.attending === "courier";
    const isInPerson =
      !isCourier &&
      (s.day || s.attending === "inPerson") &&
      s.day !== "" &&
      s.day !== "NA" &&
      s.day !== "N/A";
    const isRegistered = isCourier || isInPerson;

    const registrationStatus = isRegistered ? "Registered" : "Not Registered";
    const attendingMode = isCourier
      ? "Courier"
      : isInPerson
      ? "In-Person"
      : "Not Registered";

    const duesStatus = s.has_due
      ? `Pending Due (₹${s.total_due_amount || 0})`
      : "Cleared (No Dues)";

    const feedbackStatus = s.feedbackGiven ? "Submitted" : "Pending";

    const formattedDate = s.updatedAt
      ? new Date(s.updatedAt).toLocaleString("en-IN")
      : s.createdAt
      ? new Date(s.createdAt).toLocaleString("en-IN")
      : "";

    return [
      escapeCsv(s.reg_no),
      escapeCsv(s.student_name),
      escapeCsv(s.gender || ""),
      escapeCsv(s.email),
      escapeCsv(s.phone),
      escapeCsv(s.faculty),
      escapeCsv(s.school),
      escapeCsv(s.programme),
      escapeCsv(s.specialization),
      escapeCsv(s.batch),
      escapeCsv(s.cgpa),
      escapeCsv(s.credits),
      escapeCsv(registrationStatus),
      escapeCsv(attendingMode),
      escapeCsv(s.day || (isRegistered ? "N/A" : "")),
      escapeCsv(s.companions || (isInPerson ? "0" : "")),
      escapeCsv(duesStatus),
      escapeCsv(s.has_due ? s.total_due_amount || 0 : 0),
      escapeCsv(feedbackStatus),
      escapeCsv(s.address),
      escapeCsv(s.city),
      escapeCsv(s.district),
      escapeCsv(s.state),
      escapeCsv(s.country || "India"),
      escapeCsv(s.pincode),
      escapeCsv(s.account_holder_name),
      escapeCsv(s.bank_name),
      escapeCsv(s.account_number ? `\t${s.account_number}` : ""), // tab prefix prevents Excel from scientific notation on large account numbers
      escapeCsv(s.ifsc_code),
      escapeCsv(s.branch_name),
      escapeCsv(s.bank_address),
      escapeCsv(s.aadhar_front_picture),
      escapeCsv(s.aadhar_back_picture),
      escapeCsv(s.cancel_check),
      escapeCsv(formattedDate),
    ];
  });

  // Include UTF-8 BOM (\uFEFF) so Excel opens UTF-8 properly without mangling characters
  const csvContent =
    "\uFEFF" +
    [headers.map(escapeCsv).join(","), ...rows.map((r) => r.join(","))].join(
      "\r\n"
    );

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `muj_convocation_graduates_${new Date().toISOString().slice(0, 10)}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

